import {
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { CustomEvent, Map } from "schemas/functions";

import StyledTextField from "components/Styled/TextField";
import {
  SocialMediaFields,
  isValidUsername,
  sanitizeInput,
} from "features/Auth/Onboarding/AboutForm/helpers";
import styles from "features/Auth/Onboarding/styles";
import { makeDeepCopy } from "utils/updateLocalState";

const PLATFORM_DEFAULT = "instagram";

interface EngagementFormProps {
  handleBack?: () => void;
  handleNext?: (onboardingForm?: Map) => void;
  handleSkip?: () => void;
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  isProfilePage?: boolean;
}

const EngagementSocialMediaPlatforms = [
  {
    name: "Instagram",
    key: "instagram",
  },
  {
    name: "TikTok",
    key: "tiktok",
  },
  {
    name: "YouTube",
    key: "youtube",
  },
  {
    name: "Twitch",
    key: "twitch",
  },
];

const EngagementForm = ({
  handleBack,
  handleNext,
  handleSkip,
  onboardingForm,
  setOnboardingForm,
  isProfilePage = false,
}: EngagementFormProps) => {
  const [usernameValidation, setUsernameValidation] = useState<
    string | undefined
  >();

  const handlePlatformChange = (e: CustomEvent) => {
    const { value } = e.target;
    const previousPlatform =
      onboardingForm.primarySocialMediaPlatform || PLATFORM_DEFAULT;
    const previousValue = onboardingForm[`${previousPlatform}Url`];
    if (previousValue) {
      onboardingForm[`${previousPlatform}Url`] = undefined;
    }

    const copy = makeDeepCopy(onboardingForm);
    copy.primarySocialMediaPlatform = value;
    if (previousValue) {
      copy[`${value}Url`] = value;
    }
    setOnboardingForm(copy);
  };

  const handleSocialMediaBlurred = (e: CustomEvent) => {
    const { value } = e.target;

    const sanitizedInput = sanitizeInput(value.trim());

    if (sanitizedInput && !isValidUsername(sanitizedInput)) {
      setUsernameValidation(
        "Must contain only letters, numbers, underscores, hyphens or periods",
      );
    } else {
      setUsernameValidation(undefined);
    }
  };

  const handleSocialMediaChange = (e: CustomEvent) => {
    const { name, value } = e.target;

    const sanitizedInput = sanitizeInput(value.trim());

    const socialMediaUrl = `${SocialMediaFields[name]?.url}${sanitizedInput}`;

    const copy = makeDeepCopy(onboardingForm);
    copy[name] = socialMediaUrl;
    setOnboardingForm(copy);

    if (isValidUsername(value)) {
      setUsernameValidation(undefined);
    }
  };

  const onSubmit = (e: CustomEvent) => {
    e.preventDefault();

    const copyForm = { ...onboardingForm };
    let isEdited = false;

    if (usernameValidation) {
      return;
    }

    if (
      !onboardingForm.primarySocialMediaPlatform &&
      onboardingForm.instagramUrl
    ) {
      copyForm.primarySocialMediaPlatform = PLATFORM_DEFAULT;
      isEdited = true;
    }

    if (
      copyForm[`${copyForm.primarySocialMediaPlatform}Url`] ===
      SocialMediaFields[`${copyForm.primarySocialMediaPlatform}Url`]?.url
    ) {
      copyForm[`${copyForm.primarySocialMediaPlatform}Url`] = "";
      isEdited = true;
    }

    if (isEdited) {
      setOnboardingForm(copyForm);
    }

    if (handleNext) handleNext(copyForm);
  };

  const handleChange = (e: CustomEvent) => {
    const { name, value } = e.target;

    const copy = makeDeepCopy(onboardingForm);
    copy[name] = value;
    setOnboardingForm(copy);
  };

  return (
    <form onSubmit={onSubmit}>
      <Grid container columnSpacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md>
          <Typography gutterBottom>
            Primary social channel (where you have the most followers or brand
            partnerships)
          </Typography>
          <Grid container columnSpacing={2}>
            <Grid item xs={12} md="auto">
              <Select
                sx={styles.selectSocial}
                name="primarySocialMediaPlatform"
                onChange={handlePlatformChange}
                value={
                  onboardingForm.primarySocialMediaPlatform || PLATFORM_DEFAULT
                }
              >
                {EngagementSocialMediaPlatforms.map((platform) => (
                  <MenuItem key={platform.key} value={platform.key}>
                    {platform.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md>
              <StyledTextField
                fullWidth
                name={`${
                  onboardingForm.primarySocialMediaPlatform || PLATFORM_DEFAULT
                }Url`}
                placeholder="handle"
                error={!!usernameValidation}
                helperText={usernameValidation}
                onChange={handleSocialMediaChange}
                onBlur={handleSocialMediaBlurred}
                value={
                  sanitizeInput(
                    onboardingForm?.[
                      `${
                        onboardingForm.primarySocialMediaPlatform ||
                        PLATFORM_DEFAULT
                      }Url`
                    ],
                  ) || ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      sx={styles.linkAdornment}
                      disablePointerEvents
                      position="start"
                    >
                      {
                        SocialMediaFields[
                          `${
                            onboardingForm.primarySocialMediaPlatform ||
                            PLATFORM_DEFAULT
                          }Url`
                        ]?.url
                      }
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={3}>
          <StyledTextField
            fullWidth
            label="Number of followers"
            name="numOfFollowers"
            placeholder="134K"
            onChange={handleChange}
            value={onboardingForm?.numOfFollowers || ""}
          />
        </Grid>
      </Grid>

      <StyledTextField
        fullWidth
        label="What kind of engagement results have you seen from your content?"
        name="engagement"
        multiline
        minRows={2}
        placeholder="My posts generate between 2-3K engagements (roughly a 6% engagement rate) and here is an example of a viral post I recently did - https://www.instagram.com/p/C4pwcJLPaBV/"
        onChange={handleChange}
        value={onboardingForm?.engagement || ""}
        styles={{ mb: 2 }}
      />
    </form>
  );
};

export default EngagementForm;
